import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Scan } from 'lucide-react';
import useCameraViewport from '../utils/useCameraViewport';

/**
 * PhotoScanGuideModal — pre-scan onboarding overlay shown before the
 * SelfTimerCamera opens. Gives the user a "high-tech clinical" sense by
 * showing 3 silhouette diagrams (Face / Profil / Dos) with a BlazePose
 * skeleton watermark, pose-specific instructions, and a pagination
 * indicator.
 *
 * Props:
 *  open         boolean
 *  onClose      () => void
 *  onStart      () => void  — called when "Démarrer le scan" is pressed
 *  photoType    'face' | 'profile' | 'dos' — initial focused pose
 *  language     'fr' | 'en'
 */

// BlazePose-ish skeleton node positions (x%, y%) on a 200x320 silhouette box.
// 13 upper-body landmarks are enough to convey the "technical scan" vibe.
const LANDMARKS_FACE = [
  [50, 14], // head
  [44, 13], [56, 13], // eyes
  [50, 20], // nose
  [36, 30], [64, 30], // shoulders
  [34, 48], [66, 48], // elbows
  [36, 66], [64, 66], // wrists
  [44, 60], [56, 60], // hips
  [44, 82], [56, 82], // knees
  [44, 96], [56, 96], // ankles
];
const EDGES_FACE = [
  [0, 3], [3, 4], [3, 5], [4, 5],
  [4, 6], [5, 7], [6, 8], [7, 9],
  [4, 10], [5, 11], [10, 11],
  [10, 12], [11, 13], [12, 14], [13, 15],
];

const LANDMARKS_PROFILE = [
  [52, 14], // head
  [56, 18], // nose/eye
  [48, 30], // shoulder
  [48, 50], // elbow
  [50, 68], // wrist
  [46, 58], // hip
  [48, 80], // knee
  [50, 96], // ankle
];
const EDGES_PROFILE = [
  [0, 1], [0, 2], [2, 3], [3, 4],
  [2, 5], [5, 6], [6, 7],
];

const LANDMARKS_DOS = [
  [50, 14], // head
  [36, 30], [64, 30], // shoulders
  [33, 48], [67, 48], // elbows
  [36, 66], [64, 66], // wrists
  [44, 60], [56, 60], // hips
  [44, 82], [56, 82], // knees
  [44, 96], [56, 96], // ankles
];
const EDGES_DOS = [
  [0, 1], [0, 2], [1, 2],
  [1, 3], [2, 4], [3, 5], [4, 6],
  [1, 7], [2, 8], [7, 8],
  [7, 9], [8, 10], [9, 11], [10, 12],
];

const POSES = [
  {
    key: 'face',
    silhouettePath:
      // Front-facing rough silhouette (symmetric)
      'M50,8 C58,8 64,14 64,22 C64,28 60,32 54,33 L58,36 L68,38 C74,40 76,44 76,50 L74,62 L72,80 L70,98 L62,100 L60,84 L58,68 L52,68 L52,100 L48,100 L48,68 L42,68 L40,84 L38,100 L30,98 L28,80 L26,62 L24,50 C24,44 26,40 32,38 L42,36 L46,33 C40,32 36,28 36,22 C36,14 42,8 50,8 Z',
    landmarks: LANDMARKS_FACE,
    edges: EDGES_FACE,
    titleFr: 'FACE',
    titleEn: 'FRONT',
    instructionFr: 'Regarde droit devant, bras détendus.',
    instructionEn: 'Look straight ahead, arms relaxed.',
  },
  {
    key: 'profile',
    silhouettePath:
      // Profile silhouette (head leans slightly, belly line curved)
      'M52,8 C60,8 66,14 66,22 C66,28 62,32 58,33 L62,36 L64,42 L62,50 L60,62 L60,80 L60,100 L50,100 L48,80 L46,62 L44,50 L44,40 L48,35 L52,34 C48,32 46,28 46,22 C46,14 48,8 52,8 Z',
    landmarks: LANDMARKS_PROFILE,
    edges: EDGES_PROFILE,
    titleFr: 'PROFIL',
    titleEn: 'PROFILE',
    instructionFr: 'Pieds joints, regarde à l\'horizon.',
    instructionEn: 'Feet together, eyes on the horizon.',
  },
  {
    key: 'dos',
    silhouettePath:
      // Back-facing silhouette with mild taper (V-shape)
      'M50,8 C58,8 64,14 64,22 C64,28 60,32 54,33 L58,36 L70,40 C76,42 78,46 77,52 L74,62 L71,78 L69,98 L60,100 L58,84 L56,68 L52,68 L52,100 L48,100 L48,68 L44,68 L42,84 L40,100 L31,98 L29,78 L26,62 L23,52 C22,46 24,42 30,40 L42,36 L46,33 C40,32 36,28 36,22 C36,14 42,8 50,8 Z',
    landmarks: LANDMARKS_DOS,
    edges: EDGES_DOS,
    titleFr: 'DOS',
    titleEn: 'BACK',
    instructionFr: 'Contracte tes muscles dorsaux légèrement.',
    instructionEn: 'Engage your back muscles lightly.',
  },
];

function PoseSilhouette({ pose, active }) {
  // 100x100 viewBox for the silhouette, landmarks on same coordinate system
  return (
    <svg
      viewBox="0 0 100 100"
      className={`w-full h-full transition-all duration-500 ${active ? 'opacity-100' : 'opacity-40'}`}
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id={`body-grad-${pose.key}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.32" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.12" />
        </linearGradient>
      </defs>

      {/* Silhouette body fill (toned down so skeleton reads clearly) */}
      <path
        d={pose.silhouettePath}
        fill={`url(#body-grad-${pose.key})`}
        stroke="hsl(var(--primary))"
        strokeOpacity="0.85"
        strokeWidth="0.6"
      />

      {/* BlazePose skeleton watermark — edges (cyan-white for clinical contrast) */}
      <g stroke="#e6fbff" strokeOpacity="1" strokeWidth="0.85" strokeLinecap="round" style={{ filter: 'drop-shadow(0 0 0.6px rgba(255,255,255,0.7))' }}>
        {pose.edges.map(([a, b], i) => {
          const p1 = pose.landmarks[a];
          const p2 = pose.landmarks[b];
          if (!p1 || !p2) return null;
          return (
            <line
              key={i}
              x1={p1[0]} y1={p1[1]}
              x2={p2[0]} y2={p2[1]}
            />
          );
        })}
      </g>

      {/* BlazePose skeleton watermark — landmark dots (bigger + glowing core) */}
      <g>
        {pose.landmarks.map(([x, y], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r="1.8" fill="#ffffff" opacity="0.25" />
            <circle cx={x} cy={y} r="1.15" fill="#ffffff" stroke="hsl(var(--primary))" strokeWidth="0.35" />
          </g>
        ))}
      </g>

      {/* Corner brackets (clinical feel) */}
      <g stroke="hsl(var(--primary))" strokeWidth="0.5" fill="none" opacity="0.8">
        <path d="M4,10 L4,4 L10,4" />
        <path d="M90,4 L96,4 L96,10" />
        <path d="M4,90 L4,96 L10,96" />
        <path d="M96,90 L96,96 L90,96" />
      </g>
    </svg>
  );
}

export default function PhotoScanGuideModal({
  open,
  onClose,
  onStart,
  photoType = 'face',
  language = 'fr',
}) {
  const fr = language === 'fr';
  const initialIdx = Math.max(0, POSES.findIndex(p => p.key === photoType));
  const [idx, setIdx] = useState(initialIdx === -1 ? 0 : initialIdx);

  // Reset pagination when modal opens (so it always matches the user's chosen angle)
  useEffect(() => {
    if (open) {
      const i = POSES.findIndex(p => p.key === photoType);
      setIdx(i >= 0 ? i : 0);
    }
  }, [open, photoType]);

  // Iter. 406.d — iOS-safe full-viewport shell (called unconditionally to
  // respect the Rules of Hooks; the hook itself no-ops when `open` is false).
  const { containerStyle } = useCameraViewport({ open });

  if (!open) return null;

  const pose = POSES[idx];
  const atFirst = idx === 0;
  const atLast = idx === POSES.length - 1;

  return (
    <div
      className="fixed inset-0 z-[1000] bg-background/95 backdrop-blur-md flex flex-col"
      style={containerStyle}
      data-testid="photo-scan-guide-modal"
      role="dialog"
      aria-modal="true"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Scan className="w-4 h-4 text-primary" />
          <span className="text-[11px] font-heading uppercase tracking-[0.2em] text-primary">
            {fr ? 'Scan Clinique · BlazePose' : 'Clinical Scan · BlazePose'}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-9 h-9 rounded-full hover:bg-muted flex items-center justify-center transition"
          aria-label={fr ? 'Fermer' : 'Close'}
          data-testid="scan-guide-close-btn"
        >
          <X className="w-5 h-5 text-foreground" />
        </button>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-y-auto px-5 py-6 flex flex-col">
        {/* Silhouette carousel */}
        <div className="relative flex-1 flex items-center justify-center">
          {/* Previous pose (left, faded) */}
          {!atFirst && (
            <button
              type="button"
              onClick={() => setIdx(idx - 1)}
              className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-card border border-border hover:bg-muted flex items-center justify-center transition z-10"
              aria-label={fr ? 'Précédent' : 'Previous'}
              data-testid="scan-guide-prev-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}

          <div
            className="flex items-center justify-center"
            style={{ width: '100%', maxWidth: '260px', aspectRatio: '5 / 8' }}
          >
            <PoseSilhouette pose={pose} active />
          </div>

          {!atLast && (
            <button
              type="button"
              onClick={() => setIdx(idx + 1)}
              className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-card border border-border hover:bg-muted flex items-center justify-center transition z-10"
              aria-label={fr ? 'Suivant' : 'Next'}
              data-testid="scan-guide-next-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Pose title + instruction */}
        <div className="text-center mt-6 space-y-2">
          <h3 className="font-heading text-2xl tracking-[0.25em] text-white" data-testid="scan-guide-pose-title">
            {fr ? pose.titleFr : pose.titleEn}
          </h3>
          <p className="text-sm text-muted-foreground max-w-xs mx-auto leading-relaxed" data-testid="scan-guide-instruction">
            {fr ? pose.instructionFr : pose.instructionEn}
          </p>
        </div>

        {/* Pagination dots */}
        <div className="flex items-center justify-center gap-2 mt-5" data-testid="scan-guide-pagination">
          {POSES.map((p, i) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setIdx(i)}
              aria-label={p.key}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? 'w-7 bg-primary' : 'w-1.5 bg-muted-foreground/40 hover:bg-muted-foreground/70'
              }`}
              data-testid={`scan-guide-dot-${p.key}`}
            />
          ))}
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="px-5 pb-[max(env(safe-area-inset-bottom),1.25rem)] pt-4 border-t border-border bg-card/40">
        <button
          type="button"
          onClick={onStart}
          className="w-full h-12 rounded-full bg-primary text-primary-foreground font-heading uppercase tracking-[0.25em] text-sm hover:bg-primary/90 active:scale-[0.98] transition flex items-center justify-center gap-2"
          data-testid="scan-guide-start-btn"
        >
          <Scan className="w-4 h-4" />
          {fr ? 'Démarrer le scan' : 'Start the scan'}
        </button>
        <p className="text-center text-[10px] text-muted-foreground mt-2 tracking-wider uppercase">
          {fr
            ? 'Alignement · Détection auto · Capture sécurisée'
            : 'Alignment · Auto-detect · Secure capture'}
        </p>
      </div>
    </div>
  );
}
